import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * 服务器时间 Store
 * 存储服务器返回的最新时间戳
 *
 * @example 基本使用
 * ```ts
 * import { useServerTimeStore } from '@/stores/serverTime'
 *
 * const serverTimeStore = useServerTimeStore()
 *
 * // 获取最新的服务器时间戳
 * const serverTs = serverTimeStore.serverTs
 *
 * // 获取最后更新时间
 * const lastUpdated = serverTimeStore.lastUpdated
 * ```
 */
export const useServerTimeStore = defineStore('serverTime', () => {
  // 服务器时间戳
  const serverTs = ref<number>(0)

  // 最后更新时间（本地时间戳）
  const lastUpdated = ref<number>(0)

  /**
   * 更新服务器时间戳
   * @param ts 服务器返回的时间戳
   */
  function updateServerTime(ts: number) {
    // 仅在新的时间戳大于当前存储的时间戳时更新
    if (ts > serverTs.value) {
      serverTs.value = ts
      lastUpdated.value = Date.now()
    }
  }

  /**
   * 获取当前服务器时间（秒）
   * 基于最后一次同步的服务器时间 + 本地流逝时间
   */
  function getCurrentServerTime(): number {
    if (!serverTs.value || !lastUpdated.value) {
      return Math.floor(Date.now() / 1000)
    }
    const localElapsed = Math.floor((Date.now() - lastUpdated.value) / 1000)
    return serverTs.value + localElapsed
  }

  return {
    serverTs,
    lastUpdated,
    updateServerTime,
    getCurrentServerTime,
  }
})
